import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { GetStyleTreeRequest, SaveStyleCategoryRequest, SaveStyleCategoryResponse, SituViewWebApiClient, StyleCategoryNode, StyleNode, UpdateCategoryStylesRequest, UpdateCategoryStylesResponse, AddStyleCategoryRequest, AddStyleCategoryResponse } from 'situview-webapi-client';
import { Company } from 'identity-webapi-client';
import { animate, keyframes, transition, trigger } from '@angular/animations';
import * as kf from '../../../../core/keyframes';
import { Guid } from 'guid-typescript';
import { Subscription } from 'rxjs';
import { AuthService, BreadcrumbService, StateService } from 'src/app/services';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';

/** Flat node with expandable and level information */
interface FlatNode {
  expandable: boolean;
  name: string;
  level: number;
  data: StyleCategoryNode | StyleNode;
}

@Component({
  selector: 'app-style-tree',
  templateUrl: './style-tree.component.html',
  styleUrls: ['./style-tree.component.scss'],
  animations: [
    trigger('cardAnimator', [
      transition('* => headShake', animate(1000, keyframes(kf.headShake)))
    ])
  ]
})
export class StyleTreeComponent extends SituBaseComponent {
  @ViewChild('addCategoryButton') addCategoryButton: ElementRef<HTMLButtonElement> | undefined;
  animateAddCategory: string = '';

  private _loading = false;
  public get loading(): boolean {
    return this._loading;
  }

  public set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  //admin = true;
  collapsed: boolean = false;
  //company: Company | undefined;

  styleTree: StyleCategoryNode[] = [];
  treeControl: FlatTreeControl<FlatNode>;
  treeFlattener: MatTreeFlattener<StyleCategoryNode | StyleNode, FlatNode, FlatNode>;
  dataSource: MatTreeFlatDataSource<StyleCategoryNode | StyleNode, FlatNode, FlatNode>;
  categories: StyleCategoryNode[] = [];

  selectedCategory: StyleCategoryNode | undefined;

  allStyles: StyleNode[] = [];

  // UI state
  searchTerm: string = '';
  filteredCategories: StyleCategoryNode[] = [];

  constructor(
    authService: AuthService,
    stateService: StateService,
    private situViewClient: SituViewWebApiClient,
    private breadcrumbService: BreadcrumbService,
    private snackBar: MatSnackBar) {
      super(authService, stateService);
      this.treeControl = new FlatTreeControl<FlatNode>(
      node => node.level,
      node => node.expandable,
    );

    this.treeFlattener = new MatTreeFlattener(
      this._transformer,
      node => node.level,
      node => node.expandable,
      node => [...(node as StyleCategoryNode).styleCategories ?? [], ...(node as StyleCategoryNode).styles ?? []] as (StyleCategoryNode | StyleNode)[],
    );

    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
  }

  protected onSelectedCompanyChange(): void {
    this.loadStyleTree();
  }

  hasChild = (_: number, node: FlatNode) => node.expandable;

  getThemeDisplayName(themeId: string, name: string) {
    if (!this.styleTree) return name;
    for (const category of this.styleTree) {
      const matchingStyle = category?.styles?.find((style: any) => style.id === themeId);
      if (matchingStyle) {
        return matchingStyle.displayName;
      }
    }
    return name;
  }

  addStyleCategory() {
    const styleCategory = new StyleCategoryNode({
      id: Guid.create().toString(),
      companyId: this.selectedCompany?.id,
      name: 'New Category',
      parentStyleCategoryId: undefined,
      styles: [],
      styleCategories: []
    });

    this.dataSource.data = [...this.dataSource.data, styleCategory];

    this.selectedCategory = styleCategory;

    // save this on the server
    let request = new AddStyleCategoryRequest();
    request.companyId = styleCategory.companyId;
    request.name = styleCategory.name;
    
    this.situViewClient.addStyleCategory(request)
      .then((x: AddStyleCategoryResponse) => {
        if (x.success) {
          styleCategory.id = x.styleCategoryId;
          this.snackBar.open(`Added new category!`, 'OK', { duration: 2000 });
        } else {
          this.snackBar.open(`Error adding category!`, 'OK', { duration: 2000 });
        }
      })
      .catch((err: any) => {
        this.snackBar.open(`Error adding category!`, 'OK', { duration: 2000 });
      });
  }

  editStyleCategory(category: FlatNode | StyleCategoryNode) {
    const categoryData = (category as FlatNode).data || (category as StyleCategoryNode);
    if(categoryData.id == "00000000-0000-0000-0000-000000000000"){
      return;
    }
    this.selectedCategory = categoryData;
  }

  addStyle() {
    this.dataSource.data.push(new StyleNode({
      id: 's55',
      companyId: '1',
      displayName: 'Style 55',
      styleCategoryId: ''
    }));
  }

  remove(node: StyleCategoryNode | StyleNode) {
    //this.dataSource.data.splice(node);
  }

  loadStyleTree() {
    const request = new GetStyleTreeRequest();
    request.companyId = this.selectedCompany?.id;
    request.includeOrphaned = this.isAdmin;

    this.loading = true;
    this.situViewClient.getStyleTree(request).then(response => {
      if (response.success) {
        this.styleTree = response.categories ?? [];
        this.dataSource.data = this.styleTree;
        this.categories = this.getFlatCategories(this.styleTree);
        this.allStyles = this.getFlatStyles(this.styleTree);
      }
      this.loading = false;
    }, error => {
      this.loading = false;
    });
  }

  saveCategory(categoryNode: StyleCategoryNode) {
    if (!categoryNode?.id) {
      return;
    }

    // move the category to the new parent
    let currentParent = this.findParentOfStyleCategory(categoryNode.id, this.dataSource.data);
    if (currentParent?.id != categoryNode.parentStyleCategoryId) {
      // remove from current parent
      if (currentParent) {
        currentParent.styleCategories = currentParent.styleCategories?.filter(x => x.id != categoryNode.id);
      }

      // add to new parent
      if (categoryNode.parentStyleCategoryId) {
        let newParent = this.findStyleCategoryById(categoryNode.parentStyleCategoryId, this.dataSource.data);
        newParent!.styleCategories ??= [];
        newParent?.styleCategories.push(categoryNode);
      }
    }

    // trigger ui refresh
    this.dataSource.data = this.dataSource.data;

    // save to the server
    let request = new SaveStyleCategoryRequest();
    request.styleCategoryId = categoryNode.id;
    request.companyId = categoryNode.companyId;
    request.name = categoryNode.name;
    request.parentStyleCategoryId = categoryNode.parentStyleCategoryId;
    request.description = categoryNode.description;
    request.isRequired = categoryNode.isRequired;
    request.isSingleSelect = categoryNode.isSingleSelect;
    
    this.situViewClient.saveStyleCategory(request)
    .then((x: SaveStyleCategoryResponse) => {
      if (x.success) {
        this.snackBar.open(`${this.selectedCategory?.name} category updated!`, 'OK', { duration: 2000 });
      } else {
        this.snackBar.open(`Error updating ${this.selectedCategory?.name} category!`, 'OK', { duration: 2000 });
      }
    })
    .catch((err: any) => {
      this.snackBar.open(`Error updating ${this.selectedCategory?.name} category!`, 'OK', { duration: 2000 });
    });
  }

  saveCategoryStyles(styles: StyleNode[]) {
    if (!this.selectedCategory) {
      return;
    }

    // todo: add styles to 'other' that were removed
    // but, for now they will disappear
    let removedStyles = this.selectedCategory?.styles?.filter(x => !styles.some(y => y.id == x.id)) ?? [];
    removedStyles.forEach(x => x.styleCategoryId = undefined);

    // remove styles taken from other categories
    let newStyles = styles.filter(x => x.styleCategoryId && x.styleCategoryId != this.selectedCategory?.id) ?? [];
    newStyles.forEach(newStyle => {
      let category = this.findStyleCategoryById(newStyle.styleCategoryId!, this.dataSource.data);
      category!.styles = category?.styles?.filter(x => x.id != newStyle.id);
    });

    // update selected category styles
    this.selectedCategory.styles = this.allStyles.filter(x => styles.some(y => y.id == x.id)) ?? [];
    this.selectedCategory.styles!.forEach(x => x.styleCategoryId = this.selectedCategory?.id);

    // trigger ui refresh
    this.refreshTree();

    // save to the server
    let request = new UpdateCategoryStylesRequest();
    request.styleCategoryId = this.selectedCategory.id;
    request.companyId = this.selectedCategory.companyId;
    request.styleIds = this.selectedCategory.styles?.map(x => x.id as string) ?? [];
        
    this.situViewClient.updateCategoryStyles(request)
      .then((x: UpdateCategoryStylesResponse) => {
        if (x.success) {
          this.snackBar.open(`${this.selectedCategory?.name} category styles updated!`, 'OK', { duration: 2000 });
        } else {
          this.snackBar.open(`Error updating ${this.selectedCategory?.name} category styles!`, 'OK', { duration: 2000 });
        }
      })
      .catch((err: any) => {
        this.snackBar.open(`Error updating ${this.selectedCategory?.name} category styles!`, 'OK', { duration: 2000 });
      });
  }

  refreshTree() {
    // get the current state of the expanded nodes
    const expandedNodes = new Set<string>();
    this.treeControl.dataNodes.forEach(item => {
      if (this.treeControl.isExpanded(item)) {
        expandedNodes.add(item.data.id ?? '');
      }
    });

    // trigger ui refresh
    this.dataSource.data = this.dataSource.data;

    // restore the state of the expanded nodes
    expandedNodes.forEach(nodeId => {
      const node = this.treeControl.dataNodes.find(x => x.data.id === nodeId);
      if (node) {
        this.treeControl.expand(node);
      }
    });
  }

  findStyleCategoryById(id: string, categories: StyleCategoryNode[]): StyleCategoryNode | undefined {
    for (const category of categories) {
      if (category.id === id) {
        return category;
      }
      if (category.styleCategories) {
        const result = this.findStyleCategoryById(id, category.styleCategories);
        if (result) {
          return result;
        }
      }
    }
    return undefined;
  }

  findParentOfStyleCategory(id: string, categories: StyleCategoryNode[]): StyleCategoryNode | undefined {
    for (const category of categories) {
      if (category.styleCategories && category.styleCategories.length > 0) {
        // Check if any child category has the specified id
        const childCategory = category.styleCategories.find(child => child.id === id);
        if (childCategory) {
          return category;
        } else {
          // Recursively search the child categories for the specified id
          const parentCategory = this.findParentOfStyleCategory(id, category.styleCategories);
          if (parentCategory) {
            return parentCategory;
          }
        }
      }
    }
    // If the category with the specified id is not found, return undefined
    return undefined;
  }

  startAnimation(state: string) {
    if (!this.animateAddCategory) {
      this.animateAddCategory = state;
    }
  }

  resetAddCategoryAnimationState() {
    this.animateAddCategory = '';
  }

  getFlatCategories(categoryTree: StyleCategoryNode[]): StyleCategoryNode[] {
    const flattened: StyleCategoryNode[] = [];

    function flattenHelper(node: StyleCategoryNode) {
      flattened.push(node);
      if (node.styleCategories) {
        for (const childNode of node.styleCategories) {
          flattenHelper(childNode);
        }
      }
    }

    for (const node of categoryTree) {
      flattenHelper(node);
    }

    return flattened;
  }

  getFlatStyles(categoryTree: StyleCategoryNode[]): StyleNode[] {
    const flattened: StyleNode[] = [];

    function flattenHelper(node: StyleCategoryNode) {
      flattened.push(...node.styles ?? []);
      if (node.styleCategories) {
        for (const childNode of node.styleCategories) {
          flattenHelper(childNode);
        }
      }
    }

    for (const node of categoryTree) {
      flattenHelper(node);
    }

    return flattened;
  }

  clearCategory() {
    this.selectedCategory = undefined;
  };

  // UI Helper Methods
  getFilteredCategories(): StyleCategoryNode[] {
    if (!this.searchTerm) {
      return this.styleTree;
    }
    
    return this.styleTree.filter(category =>
      category.name?.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
      category.description?.toLowerCase().includes(this.searchTerm.toLowerCase())
    );
  }

  onSearchChange(): void {
    // Filter will be applied automatically via getFilteredCategories()
  }

  hasChildCategories(category: StyleCategoryNode): boolean {
    return !!(category.styleCategories && category.styleCategories.length > 0);
  }

  getStyleCount(category: StyleCategoryNode): number {
    return category.styles?.length || 0;
  }

  getChildCategoryCount(category: StyleCategoryNode): number {
    return category.styleCategories?.length || 0;
  }

  getPreviewStyles(category: StyleCategoryNode): StyleNode[] {
    return category.styles?.slice(0, 3) || [];
  }

  getCategoryIcon(category: StyleCategoryNode): string {
    if (category.name?.toLowerCase().includes('theme')) return 'palette';
    if (category.name?.toLowerCase().includes('furnishing')) return 'chair';
    if (category.name?.toLowerCase().includes('decorator')) return 'brush';
    if (category.name?.toLowerCase().includes('placement')) return 'place';
    return 'category';
  }

  getCategoryColor(category: StyleCategoryNode): string {
    const colors = ['#1976d2', '#388e3c', '#f57c00', '#7b1fa2', '#c2185b', '#00796b'];
    const index = Math.abs(category.name?.length || 0) % colors.length;
    return colors[index];
  }

  viewCategoryDetails(category: StyleCategoryNode): void {
    // For now, just edit the category
    this.editStyleCategory(category);
  }

  duplicateCategory(category: StyleCategoryNode): void {
    // TODO: Implement duplication logic
    this.snackBar.open('Duplicate feature coming soon!', 'OK', { duration: 2000 });
  }


  private _transformer = (node: StyleCategoryNode | StyleNode, level: number) => {
    return {
      expandable: !(node as StyleNode).styleCategoryId || (node as StyleCategoryNode).name != undefined,
      name: (node as StyleCategoryNode).name ?? (node as StyleNode).displayName,
      level: level,
      data: node
    } as FlatNode;
  };
}
